import { useFlags as useLDFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { FeatureFlagOverrideContext } from '@/common/providers/featureFlagOverrideContext';
import type { FlagKeys } from '@/common/providers/featureFlagProvider';
import { isDevToolsEnabled } from '@/common/utils/dev-tools/isDevToolsEnabled';

/**
 * Custom hook that wraps LaunchDarkly's useFlags and applies local overrides.
 * Overrides only apply in development/staging environments.
 * Falls back to LaunchDarkly flags if the override context is not available (e.g., in tests).
 *
 * Usage:
 *   const { myFlag } = useFlags<FlagKeys>();
 */
export const useFlags = <T extends FlagKeys = FlagKeys>(): T => {
  const ldFlags = useLDFlags<T>();
  const context = useContext(FeatureFlagOverrideContext);

  if (!isDevToolsEnabled()) {
    return ldFlags;
  }

  if (!context) {
    return ldFlags;
  }

  const { overrides } = context;

  return {
    ...ldFlags,
    ...overrides,
  } as T;
};

export const useFlagOverrides = (): Partial<FlagKeys> => {
  const context = useContext(FeatureFlagOverrideContext);
  if (!isDevToolsEnabled() || !context) {
    return {};
  }

  return context.overrides;
};
